(() => {
  'use strict';
  const MODEL_URL = 'https://huggingface.co/Ko033/isnet-general-use-onnx/resolve/5349b617911fd60c619b52f32e2b593517b78df3/onnx/model_quantized.onnx';
  const MODEL_BYTES = 45902969;
  const MODEL_SHA256 = '5039225b9a4ac3df55f185d24b7a92d640c86cc4747002d7f23351e394de03a6';
  const RUNTIME_BASE = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.22.0/dist/';
  const SIZE = 512;
  let worker = null, workerUrl = null, serial = 0;
  const jobs = new Map();

  function workerMain(config) {
    let sessionPromise = null, chain = Promise.resolve();
    const progress = (id, phase, percent) => self.postMessage({id, phase, percent});
    const hex = buffer => Array.from(new Uint8Array(buffer), x => x.toString(16).padStart(2, '0')).join('');
    async function verify(buffer) {
      if (buffer.byteLength !== config.modelBytes) throw Error('model_integrity');
      if (hex(await crypto.subtle.digest('SHA-256', buffer)) !== config.modelSha256) throw Error('model_integrity');
      return buffer;
    }
    async function modelBytes(id) {
      let cache = null;
      try {
        cache = await caches.open('sd-photo-isnet-q8-5349b617-v1');
        const cached = await cache.match(config.modelUrl);
        if (cached) {
          try { return await verify(await cached.arrayBuffer()); }
          catch (_) { await cache.delete(config.modelUrl); }
        }
      } catch (_) { cache = null; }
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 90000);
      try {
        const response = await fetch(config.modelUrl, {
          mode: 'cors', credentials: 'omit', referrerPolicy: 'no-referrer',
          cache: 'force-cache', signal: controller.signal
        });
        if (!response.ok) throw Error('model_download');
        const chunks = [], reader = response.body?.getReader();
        let received = 0, bytes;
        if (reader) {
          while (true) {
            const {done, value} = await reader.read();
            if (done) break;
            received += value.byteLength;
            if (received > config.modelBytes) { await reader.cancel(); throw Error('model_integrity'); }
            chunks.push(value);
            progress(id, 'download', Math.min(99, Math.floor(received * 100 / config.modelBytes)));
          }
          bytes = new Uint8Array(received);
          let offset = 0;
          for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.byteLength; }
        } else bytes = new Uint8Array(await response.arrayBuffer());
        const buffer = await verify(bytes.buffer);
        if (cache) {
          try { await cache.put(config.modelUrl, new Response(buffer, {headers: {'Content-Type': 'application/octet-stream'}})); }
          catch (_) { /* Storage is optional; inference must still work in private browsing. */ }
        }
        return buffer;
      } finally { clearTimeout(timer); }
    }
    async function session(id) {
      if (!sessionPromise) sessionPromise = (async () => {
        progress(id, 'download', 0);
        importScripts(config.runtimeBase + 'ort.wasm.min.js');
        ort.env.logLevel = 'error';
        ort.env.wasm.numThreads = 1;
        ort.env.wasm.proxy = false;
        ort.env.wasm.wasmPaths = config.runtimeBase;
        const bytes = await modelBytes(id);
        progress(id, 'prepare', 100);
        return ort.InferenceSession.create(bytes, {
          executionProviders: ['wasm'], graphOptimizationLevel: 'all',
          enableCpuMemArena: false, enableMemPattern: false, logSeverityLevel: 3
        });
      })().catch(error => { sessionPromise = null; throw error; });
      return sessionPromise;
    }
    function matte(values) {
      const n = config.size * config.size;
      if (values.length !== n) throw Error('mask_invalid');
      let min = Infinity, max = -Infinity;
      for (let i = 0; i < n; i++) {
        const v = values[i];
        if (!Number.isFinite(v)) throw Error('mask_invalid');
        if (v < min) min = v;
        if (v > max) max = v;
      }
      if (max < .75 || max - min < .35) throw Error('subject_not_found');
      const confidence = new Uint8Array(n), labels = new Uint32Array(n), queue = new Uint32Array(n);
      const kept = [false];
      let component = 0, retained = 0;
      for (let i = 0; i < n; i++) confidence[i] = Math.round(255 * (values[i] - min) / (max - min));
      for (let start = 0; start < n; start++) {
        if (labels[start] || confidence[start] < 96) continue;
        component++;
        let head = 0, tail = 1, peak = 0, strong = 0;
        queue[0] = start; labels[start] = component;
        while (head < tail) {
          const i = queue[head++], x = i % config.size, y = (i / config.size) | 0;
          const value = confidence[i];
          if (value > peak) peak = value;
          if (value >= 230) strong++;
          for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
            if (!dx && !dy) continue;
            const nx = x + dx, ny = y + dy;
            if (nx < 0 || nx >= config.size || ny < 0 || ny >= config.size) continue;
            const j = ny * config.size + nx;
            if (!labels[j] && confidence[j] >= 96) { labels[j] = component; queue[tail++] = j; }
          }
        }
        const keep = peak >= 230 && strong >= 4 && strong / tail >= .15;
        kept.push(keep);
        if (keep) retained += tail;
      }
      if (retained < n * .001 || retained > n * .985) throw Error('subject_not_found');
      const distance = new Uint8Array(n); distance.fill(255);
      let head = 0, tail = 0;
      for (let i = 0; i < n; i++) if (kept[labels[i]] && confidence[i] >= 230) {
        distance[i] = 0; queue[tail++] = i;
      }
      while (head < tail) {
        const i = queue[head++];
        if (distance[i] >= 3) continue;
        const x = i % config.size, y = (i / config.size) | 0;
        for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
          if (!dx && !dy) continue;
          const nx = x + dx, ny = y + dy;
          if (nx < 0 || nx >= config.size || ny < 0 || ny >= config.size) continue;
          const j = ny * config.size + nx;
          if (distance[j] === 255 && kept[labels[j]]) {
            distance[j] = distance[i] + 1; queue[tail++] = j;
          }
        }
      }
      const alpha = new Uint8ClampedArray(n);
      for (let i = 0; i < n; i++) {
        if (!kept[labels[i]] || distance[i] > 3) continue;
        const t = Math.max(0, Math.min(1, (confidence[i] - 96) / 64));
        alpha[i] = Math.round(255 * t * t * (3 - 2 * t));
      }
      return alpha;
    }
    async function run(message) {
      const {id, pixels} = message;
      let input = null, outputs = null;
      try {
        const model = await session(id);
        progress(id, 'segment', 100);
        input = new ort.Tensor('float32', new Float32Array(pixels), [1, 3, config.size, config.size]);
        outputs = await model.run({[model.inputNames[0]]: input});
        const alpha = matte(outputs[model.outputNames[0]].data);
        self.postMessage({id, alpha: alpha.buffer, width: config.size, height: config.size}, [alpha.buffer]);
      } catch (error) {
        const code = ['model_integrity', 'model_download', 'mask_invalid', 'subject_not_found'].includes(error?.message)
          ? error.message : 'processing_failed';
        self.postMessage({id, error: code});
      } finally {
        input?.dispose?.();
        if (outputs) for (const tensor of Object.values(outputs)) tensor.dispose?.();
      }
    }
    self.onmessage = event => { chain = chain.then(() => run(event.data)); };
  }

  function stop(reason = null) {
    if (worker) worker.terminate();
    worker = null;
    if (workerUrl) URL.revokeObjectURL(workerUrl);
    workerUrl = null;
    for (const job of jobs.values()) { job.cleanup(); job.reject(reason || new DOMException('Cancelled', 'AbortError')); }
    jobs.clear();
  }
  function getWorker() {
    if (worker) return worker;
    const config = {modelUrl: MODEL_URL, modelBytes: MODEL_BYTES, modelSha256: MODEL_SHA256, runtimeBase: RUNTIME_BASE, size: SIZE};
    workerUrl = URL.createObjectURL(new Blob(['(' + workerMain.toString() + ')(' + JSON.stringify(config) + ');'], {type: 'application/javascript'}));
    worker = new Worker(workerUrl);
    worker.onmessage = event => {
      const data = event.data, job = jobs.get(data.id);
      if (!job) return;
      if (data.phase) { job.progress?.(data.phase, data.percent); return; }
      job.cleanup(); jobs.delete(data.id);
      if (!jobs.size) stop(); // Release model memory immediately, including on mobile.
      if (data.error) job.reject(Error(data.error));
      else job.resolve(data);
    };
    worker.onerror = () => {
      const pending = Array.from(jobs.values());
      jobs.clear();
      stop();
      for (const job of pending) { job.cleanup(); job.reject(Error('processing_failed')); }
    };
    return worker;
  }
  async function remove(source, options = {}) {
    if (!window.Worker || !window.WebAssembly || !window.crypto?.subtle) throw Error('browser_unsupported');
    if (options.signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
    const sample = document.createElement('canvas'); sample.width = sample.height = SIZE;
    const sampleContext = sample.getContext('2d', {willReadFrequently: true});
    sampleContext.drawImage(source, 0, 0, SIZE, SIZE);
    const rgba = sampleContext.getImageData(0, 0, SIZE, SIZE).data;
    const n = SIZE * SIZE, input = new Float32Array(3 * n);
    for (let i = 0; i < n; i++) {
      input[i] = rgba[4 * i] / 255 - .5;
      input[n + i] = rgba[4 * i + 1] / 255 - .5;
      input[2 * n + i] = rgba[4 * i + 2] / 255 - .5;
    }
    const result = await new Promise((resolve, reject) => {
      const id = ++serial;
      const abort = () => stop();
      const timer = setTimeout(() => stop(Error('processing_failed')), 180000);
      const cleanup = () => { clearTimeout(timer); options.signal?.removeEventListener('abort', abort); };
      try {
        const activeWorker = getWorker();
        jobs.set(id, {resolve, reject, cleanup, progress: options.onProgress});
        options.signal?.addEventListener('abort', abort, {once: true});
        activeWorker.postMessage({id, pixels: input.buffer}, [input.buffer]);
      } catch (error) { jobs.delete(id); cleanup(); reject(error); }
    });
    if (options.signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
    const alpha = new Uint8ClampedArray(result.alpha);
    const matte = sampleContext.createImageData(result.width, result.height);
    for (let i = 0; i < alpha.length; i++) {
      matte.data[4 * i] = matte.data[4 * i + 1] = matte.data[4 * i + 2] = 255;
      matte.data[4 * i + 3] = alpha[i];
    }
    sampleContext.putImageData(matte, 0, 0);
    const output = document.createElement('canvas'); output.width = source.width; output.height = source.height;
    const context = output.getContext('2d');
    context.drawImage(source, 0, 0);
    context.globalCompositeOperation = 'destination-in';
    context.drawImage(sample, 0, 0, output.width, output.height);
    context.globalCompositeOperation = 'source-over';
    return output;
  }
  window.SDPhotoBackground = Object.freeze({remove, cancel: stop, version: 'isnet-q8-5349b617-matte-v3-512'});
  window.addEventListener('pagehide', () => stop());
})();
